import React, { useMemo } from 'react';
import { Box, Typography } from '@/shared/design-system/primitives';
import { toLogicDate } from '@/utils/date-utils';
import { useMetabolicStore } from '../../../stores/v4/useMetabolicStore';

interface InsulinSpikeTimelineProps {
    targetDate: Date;
    sensitivity: 'HIGH' | 'NORMAL' | 'LOW';
    consumed: any;
    thresholds: { stable: number, efficient: number };
    simulation?: any[]; // Allow external injection
}

/**
 * InsulinSpikeTimeline: Visualizes systemic insulin flux from MEV4 simulation.
 * V4.0: Dumb rendering component. All math is centralized in the Core Engine.
 */
export const InsulinSpikeTimeline: React.FC<InsulinSpikeTimelineProps> = ({ targetDate, thresholds, simulation: injectedSim }) => {
    const storeSimulation = useMetabolicStore(state => state.dailySnapshot.simulation);
    const simulation = injectedSim || storeSimulation;
    const dayEvents = useMetabolicStore(state => state.dailySnapshot.dayEvents);

    const stableRate = thresholds.stable / 4;
    const efficientRate = thresholds.efficient / 4;

    // 1. Headroom Engine
    const FIXED_MAX_Y = useMemo(() => {
        const highestPoint = simulation.length > 0 ? Math.max(...simulation.map(p => p.state.insulin)) : 0;
        return Math.max(highestPoint * 1.6, efficientRate * 1.5, 60);
    }, [simulation, efficientRate]);

    const getTopPct = (val: number) => (98 - (val / FIXED_MAX_Y * 98));

    const formatTime = (xPercent: number) => {
        const mins = (xPercent / 100) * 1440;
        return `${Math.floor(mins/60).toString().padStart(2,'0')}:${Math.floor(mins%60).toString().padStart(2,'0')}`;
    };

    // 2. Path Generators
    const { pathDataFill, pathDataStroke } = useMemo(() => {
        if (!simulation || simulation.length === 0) return { pathDataFill: "", pathDataStroke: "" };
        const scaleY = (val: number) => 98 - Math.min((val / FIXED_MAX_Y) * 98, 98);
        let dFill = "M 0 98";
        let dStroke = "";
        simulation.forEach((p, i) => { 
            const y = scaleY(p.state.insulin);
            if (i === 0) dStroke = `M ${p.x} ${y}`;
            dFill += ` L ${p.x} ${y}`; 
            dStroke += ` L ${p.x} ${y}`;
        });
        dFill += " L 100 98 Z";
        return { pathDataFill: dFill, pathDataStroke: dStroke };
    }, [simulation, FIXED_MAX_Y]);

    // 2.5 Peak Identification
    const peaks = useMemo(() => {
        if (!simulation || simulation.length < 3) return [];
        const results: any[] = [];
        const scaleY = (val: number) => 98 - Math.min((val / FIXED_MAX_Y) * 98, 98);
        
        for (let i = 1; i < simulation.length - 1; i++) {
            const prev = simulation[i-1].state.insulin;
            const curr = simulation[i].state.insulin;
            const next = simulation[i+1].state.insulin;
            if (curr > prev && curr > next && curr > 5) {
                results.push({ x: simulation[i].x, y: scaleY(curr), val: Math.round(curr) });
            }
        }
        return results;
    }, [simulation, FIXED_MAX_Y]);

    const [y, mon, d] = toLogicDate(targetDate).split('-').map(Number);
    const targetMidnightUnix = new Date(y, mon - 1, d).getTime();
    const nowUnix = new Date().getTime();
    const isToday = toLogicDate(targetDate) === toLogicDate(new Date());
    const nowX = isToday ? ((nowUnix - targetMidnightUnix) / (1440 * 60000)) * 100 : null;

    return (
        <Box style={{ height: 200, marginTop: 10, position: 'relative' }}>
            <Box style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: 65, zIndex: 20, padding: 8 } as any}>
                <Box style={{ position: 'absolute', left: 8, top: getTopPct(efficientRate) + "%", marginTop: -14 } as any}>
                    <Typography style={{ fontSize: 10, fontWeight: '900', color: '#ef4444', textShadow: '0 0 3px #000' }}>VOLATILE</Typography>
                    <Typography style={{ fontSize: 9, fontWeight: 'bold', color: '#ef4444' }}>{efficientRate.toFixed(0)}</Typography>
                </Box>
                <Box style={{ position: 'absolute', left: 8, top: getTopPct(stableRate) + "%", marginTop: -14 } as any}>
                    <Typography style={{ fontSize: 10, fontWeight: '900', color: '#f29b11', textShadow: '0 0 3px #000' }}>EFFICIENT</Typography>
                    <Typography style={{ fontSize: 9, fontWeight: 'bold', color: '#f29b11' }}>{stableRate.toFixed(0)}</Typography>
                </Box>
                <Box style={{ position: 'absolute', left: 8, bottom: 12 } as any}>
                    <Typography style={{ fontSize: 10, fontWeight: '900', color: '#2ecc71', textShadow: '0 0 3px #000' }}>STABLE</Typography>
                </Box>
            </Box>

            <svg width="100%" height="100%" viewBox="0 0 100 100" preserveAspectRatio="none" style={{ overflow: 'visible' }}>
                <defs>
                    <linearGradient id="insulin-wave-grad" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#f29b11" stopOpacity="0.1" />
                        <stop offset="100%" stopColor="#f29b11" stopOpacity="0" />
                    </linearGradient>
                </defs>

                <rect x="0" y="0" width="100" height={getTopPct(efficientRate)} fill="#ef4444" opacity="0.05" />
                <rect x="0" y={getTopPct(efficientRate)} width="100" height={(efficientRate - stableRate) / FIXED_MAX_Y * 100} fill="#f29b11" opacity="0.05" />
                <rect x="0" y={getTopPct(stableRate)} width="100" height={(98 - getTopPct(stableRate)) + "%"} fill="#2ecc71" opacity="0.05" />

                <line x1="0" y1={getTopPct(stableRate)} x2="100" y2={getTopPct(stableRate)} stroke="#2ecc71" strokeWidth="0.5" strokeDasharray="2,2" opacity="0.3" />
                <line x1="0" y1={getTopPct(efficientRate)} x2="100" y2={getTopPct(efficientRate)} stroke="#f29b11" strokeWidth="0.5" strokeDasharray="2,2" opacity="0.3" />
                <line x1="0" y1="98" x2="100" y2="98" stroke="#333" strokeWidth="1" vectorEffect="non-scaling-stroke" />

                <path d={pathDataFill} fill="url(#insulin-wave-grad)" stroke="none" />
                <path d={pathDataStroke} fill="none" stroke="#f29b11" strokeWidth="1" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />

                {isToday && nowX !== null && nowX >= 0 && nowX <= 100 && (
                    <line x1={nowX} y1="0" x2={nowX} y2="98" stroke="#ef4444" strokeWidth="1" strokeDasharray="2,2" opacity="0.8" vectorEffect="non-scaling-stroke" />
                )}
            </svg>

            {/* PEAK MARKERS & LABELS (Precise Alignment) */}
            <Box style={{ position: 'absolute', left: 0, right: 0, top: 0, bottom: 0, pointerEvents: 'none' } as any}>
                {peaks.map((p, i) => (
                    <Box key={`peak-${i}`} style={{ position: 'absolute', left: `${p.x}%`, top: `${p.y}%`, alignItems: 'center', transform: [{ translateX: '-50%' }, { translateY: '-50%' }] } as any}>
                        <Box style={{ width: 4, height: 4, borderRadius: 2, backgroundColor: '#f29b11', shadowColor: '#000', shadowOffset: { width: 0, height: 0 }, shadowOpacity: 0.5, shadowRadius: 2 }} />
                        <Typography style={{ position: 'absolute', bottom: 6, fontSize: 8, fontWeight: '900', color: '#f29b11', textShadow: '0 0 3px #000', width: 40, textAlign: 'center' }}>{p.val}</Typography>
                    </Box>
                ))}
            </Box>

            {dayEvents.map((event: any) => {
                const mealUnix = new Date(event.start_time).getTime();
                const x = ((mealUnix - targetMidnightUnix) / (1440 * 60000)) * 100;
                if (x < 0 || x > 100) return null;
                return (
                    <Box key={event.id} style={{ position: 'absolute', left: `${x}%`, top: 0, bottom: 0, width: 1, backgroundColor: 'rgba(255,255,255,0.05)', zIndex: 10 } as any} />
                );
            })}
        </Box>
    );
};
